import {
    AlertModal,
    ArrowRight,
    Box,
    Button,
    DeleteModal,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    IconButton,
    Kebab,
    showToast,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { Button as AriaButton } from "react-aria-menubutton";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { CreateAssetCustomViewSubpage } from "components/asset-management-system/custom-views/CreateAssetCustomViewSubpage";
import { CreateAssetFolderModal } from "components/asset-management-system/custom-views/CreateAssetFolderModal";
import { CollapsedFolder } from "components/icons/CollapsedFolder";
import { ExpandedFolder } from "components/icons/ExpandedFolder";
import { Folder } from "components/icons/Folder";
import { subpageContext } from "components/layout/ApplicationLayout";
import style from "components/navigation/navigation-toggle-panel.scss";
import { AUTH_CUSTOM_ASSET_VIEW_DELETE } from "domain/authority";
import { assetViewService } from "services/asset-management/AssetCustomViewService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { setViews } from "store/assetViews";
import { resolveColor } from "utils/commonFunctions";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    title: string;
    path: string;
    isAssetFolder?: boolean;
    hasChildren?: boolean;
    isFolderExpanded: boolean;
    chevronClass: string;
    isArchived?: boolean;
    isKebabOpened: (isOpen: boolean) => void;
}

export const NavigationTogglePanelAssets = (props: Props) => {
    const { t } = useTranslation();
    const user = useSelector((state: StoreState) => state.userReducer.user);
    const views = useSelector((state: StoreState) => state.assetViewsReducer.views);
    const dispatch = useDispatch();
    const allowedToCreateCustomViews = userSessionService.permittedToCreateCustomViews("assets", user);
    const allowedToViewCustomViews = userSessionService.permittedToViewCustomViews("assets", user);
    const abortControllerRef = React.useRef<AbortController>();
    const context = React.useContext(subpageContext);

    const [errorMessage, setErrorMessage] = React.useState<{ title: string; message: string }>({
        title: "",
        message: "",
    });
    const [isFolderDeletionFailedModalOpen, setIsFolderDeletionFailedModalOpen] = React.useState(false);
    const [isFolderNotEmptyModalOpen, setIsFolderNotEmptyModalOpen] = React.useState(false);
    const [isFolderCreationModalOpen, setIsFolderCreationModalOpen] = React.useState(false);
    const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = React.useState(false);

    const deduceViewUuid = () => {
        if (!props.isAssetFolder) {
            return undefined;
        }
        if (props.path.includes("/")) {
            const split = props.path.split("/");
            return split[split.length - 1];
        }
        return undefined;
    };

    const triggerContentForAssetView = () => {
        context?.setSubpage({
            content: (
                <CreateAssetCustomViewSubpage
                    parentUuid={deduceViewUuid()}
                    folders={(views ?? []).filter((view) => view.folder)}
                />
            ),
            isModal: false,
        });
    };
    const handleDeleteFolder = () => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const folderUuid = deduceViewUuid();

        if (!folderUuid) {
            return;
        }

        assetViewService
            .deleteAssetView(folderUuid, abortController)
            .then(() => {
                showToast(
                    t("AssetManagement.deleteAssetView.folderDeleteSuccessMessage", {
                        folderName: props.title,
                    }),
                    "success"
                );
                setIsDeleteFolderModalOpen(false);
                assetViewService
                    .fetchAssetRelatedViews()
                    .then((views) => dispatch(setViews(views)))
                    .catch((err) => logger.error("Failed to refresh asset views after delete:", err));
            })
            .catch((error: Error & { status: number }) => {
                if (!abortController.signal.aborted) {
                    setIsDeleteFolderModalOpen(false);

                    const isFolderNotEmpty = error.status === 400 && error.message?.includes("FOLDER_NOT_EMPTY");

                    if (isFolderNotEmpty) {
                        setIsFolderNotEmptyModalOpen(true);
                    } else {
                        setIsFolderDeletionFailedModalOpen(true);
                        setErrorMessage({
                            title: t("AssetManagement.deleteAssetView.folderNotDeletedTitle"),
                            message: t("AssetManagement.deleteAssetView.failureMessage"),
                        });
                    }
                }
            })
            .finally(() => {
                setIsDeleteFolderModalOpen(false);
            });
    };

    const createAssetFolderActions = (
        <DropdownMenu
            position="bottom-start"
            dropdownOffset="Xxs"
            onDropdownOpenChange={props.isKebabOpened}
            closeOnSelection
            flipOptions={{
                fallbackPlacements: ["top-start"],
            }}
        >
            <DropdownTrigger>
                <IconButton
                    testId={testIds.navigation.assets.assetViews.kebabMenu}
                    noHoverStyle
                    className={props.isArchived ? style.noKebab : undefined}
                    size="Sm"
                >
                    <Kebab color={resolveColor("--app-navigationPanel-kebabIconColorAsset")} />
                </IconButton>
            </DropdownTrigger>
            {!props.isArchived && (
                <DropdownContent>
                    <DropdownMenuItem
                        onClick={() => {
                            triggerContentForAssetView();
                        }}
                    >
                        {t("AssetManagement.createAssetCustomView.createGroup")}
                    </DropdownMenuItem>
                    {userSessionService.hasCustomViewPermission(
                        user?.featureLicenses,
                        [AUTH_CUSTOM_ASSET_VIEW_DELETE],
                        "assets",
                        user
                    ) &&
                        !props.hasChildren && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setIsDeleteFolderModalOpen(true)} errorStyle>
                                    {t("Common.delete")}
                                </DropdownMenuItem>
                            </>
                        )}
                </DropdownContent>
            )}
        </DropdownMenu>
    );

    return (
        allowedToViewCustomViews && (
            <>
                <Box gap="Xs" padding={"Xxs"} verticalAlignment={"Center"} horizontalAlignment={"Left"}>
                    <Box
                        verticalAlignment={"Center"}
                        className={classNames(style.menuItemBolded, style.menuItemContent)}
                    >
                        {props.isAssetFolder && (
                            <Box
                                verticalAlignment="Center"
                                className={style.menuItemContent}
                                gap={props.hasChildren ? "Xxxs" : undefined}
                            >
                                <Box verticalAlignment={"Center"}>
                                    {props.hasChildren && (
                                        <AriaButton>
                                            <ArrowRight
                                                color={resolveColor("--app-navigationPanel-iconColor")}
                                                rotateDown={props.isFolderExpanded}
                                                size={16}
                                            ></ArrowRight>
                                        </AriaButton>
                                    )}
                                </Box>
                                <Box verticalAlignment="Center" gap="Xxs" className={style.menuItemIconLabel}>
                                    {!props.hasChildren ? (
                                        <Box>
                                            <Folder size={20} color={"var(--app-navigationPanel-iconColorAsset)"} />
                                        </Box>
                                    ) : (
                                        <AriaButton
                                            data-testid={testIds.navigation.assets.assetViews.expandFolderButton}
                                            data-type={props.title}
                                        >
                                            {props.isFolderExpanded ? (
                                                <ExpandedFolder
                                                    size={20}
                                                    color={"var(--app-navigationPanel-iconColorAsset)"}
                                                />
                                            ) : (
                                                <CollapsedFolder
                                                    size={20}
                                                    color={"var(--app-navigationPanel-iconColorAsset)"}
                                                />
                                            )}
                                        </AriaButton>
                                    )}
                                    <span className={style.menuItemTruncation}>{props.title}</span>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {allowedToCreateCustomViews && props.isAssetFolder && createAssetFolderActions}
                </Box>

                <DeleteModal
                    title={t("AssetManagement.deleteAssetView.deleteFolderTitle")}
                    isOpen={isDeleteFolderModalOpen}
                    hideModal={() => setIsDeleteFolderModalOpen(false)}
                    onDelete={handleDeleteFolder}
                    textBundle={{
                        confirmationText: "DELETE",
                        deleteButtonText: t("AssetManagement.deleteAssetView.deleteFolder"),
                        cancelButtonText: t("Common.cancel"),
                    }}
                >
                    {t("AssetManagement.deleteAssetView.deleteFolderMessage", { folderName: props.title })}
                </DeleteModal>
                <AlertModal
                    variant={"Error"}
                    title={errorMessage.title}
                    isOpen={isFolderDeletionFailedModalOpen}
                    hideModal={() => setIsFolderDeletionFailedModalOpen(false)}
                    buttons={
                        <Box gap={"Sm"}>
                            <Button variant={"Primary"} onClick={() => handleDeleteFolder()}>
                                {t("Common.deleteAgain")}
                            </Button>
                            <Button variant="Secondary" onClick={() => setIsFolderDeletionFailedModalOpen(false)}>
                                {t("Common.cancel")}
                            </Button>
                        </Box>
                    }
                >
                    {errorMessage.message}
                </AlertModal>
                <AlertModal
                    variant={"Error"}
                    isOpen={isFolderNotEmptyModalOpen}
                    hideModal={() => setIsFolderNotEmptyModalOpen(false)}
                    title={t("AssetManagement.deleteAssetView.folderNotDeletedTitle")}
                    buttons={
                        <Button variant={"Primary"} onClick={() => setIsFolderNotEmptyModalOpen(false)}>
                            {t("Common.cancel")}
                        </Button>
                    }
                >
                    {t("AssetManagement.deleteAssetView.folderNotEmptyMessage")}
                </AlertModal>

                <CreateAssetFolderModal
                    isOpen={isFolderCreationModalOpen}
                    onClose={() => setIsFolderCreationModalOpen(false)}
                />
            </>
        )
    );
};
